import type { TemplateDefinition } from '../../common/overview/models/templateDefinition.types';
import { freezeMandatorySystemFields } from '../../common/overview/models/freezeMandatory';

/** The MSME field's options, as the prototype lists them. */
export const SUPPLIER_MSME_OPTIONS = [
  'Micro, Small & Medium Enterprise (MSME)',
  'Small Business',
  'Diversity Status',
] as const;

/**
 * Business Structure's options (2026-10-30), as the seed and migration V20261019110000 store them. Without them a
 * template created here saved the field with none, and the onboarding wizard drew it as a text box.
 */
export const SUPPLIER_BUSINESS_STRUCTURE_OPTIONS = [
  'Proprietorship',
  'Partnership',
  'LLP',
  'Pvt Ltd',
  'Public Ltd',
] as const;

/**
 * The Supplier Onboarding kind.
 *
 * Six steps, and the only kind whose completed form is filled in by an external
 * party. Two things make it the one that stresses the shared model hardest:
 *
 *  - the **widest field-type vocabulary** in the module -- nine types beyond the
 *    nine a user may pick, so a union covering only those nine would make this
 *    kind unstorable;
 *  - the **only conditional fields** anywhere, two of them on the last step.
 *
 * Drawn as the design16 prototype draws it (2026-10-19): steps across the top, the system fields as preview tiles,
 * Template Information with an optional Business Unit, Division and Department and no Category.
 */
export const supplierOnboardingTemplateDefinition: TemplateDefinition = {
  kind: 'SUPPLIER_ONBOARDING',
  feature: 'Supplier Onboarding',
  path: 'supplier-onboarding',
  icon: 'pi-truck',
  title: 'Supplier Onboarding Templates',
  subtitle:
    'Configure and manage the onboarding forms suppliers complete when registering on the platform.',
  searchPlaceholder: 'Search templates by name',
  allStatusLabel: 'All Status',
  createSubtitle:
    'Configure the onboarding form suppliers complete when registering on the platform.',
  /* The only kind whose saved message addresses a supplier rather than an Initiator. */
  savedMessage: 'Suppliers will now complete this form when they register.',
  /* Suppliers, not Initiators: this kind's audience is the people registering,
     and they are given the form rather than choosing it. */
  fieldRemovalConsequence: 'Suppliers will no longer be asked for it when they register.',
  /*
    The prototype reuses the Initiators sentence here; this kind keeps its own, as Initiators never see the form
    (templateCopy.test.ts) -- in the prototype's shape, naming the template.
  */
  deactivateConsequence:
    'Suppliers will no longer be given "{name}" when they register. Registrations already in progress are unaffected.',
  activateConsequence: '"{name}" will be given to suppliers when they register again.',
  blankNameFallback: 'Supplier onboarding template',
  collectsCategory: false,
  /* No Category on Template Information now; the Category master rows an older template holds stay as stored. */
  preservesCategories: true,
  /*
    Mapped to the hierarchy -- one Business Unit, Division and Department at most, each optional -- which the
    template list, the Supplier master's Active Templates, its supplier list and Use Template all go by.
  */
  collectsHierarchy: true,
  /* The entity's seeded default is neither edited nor deleted from the list; templates created or duplicated beside it are. */
  lockedTemplateName: 'Default Supplier Onboarding',
  hierarchyOptional: true,
  overviewSecondValue: 'fieldCount',
  namePlaceholder: 'e.g. Domestic Supplier Onboarding',
  descriptionPlaceholder: 'What is this template used for?',
  informationHint: 'Shown to Initiators when they pick a template.',

  /* The design16 prototype (2026-10-19). */
  topStepper: true,
  designFooter: true,
  draftSavesInPlace: true,
  /* Save as Draft returns to the list, on any step (2026-10-22). */
  draftReturnsToList: true,
  /* Save Template with the Status toggle off saves Inactive; only Save as Draft saves Draft (2026-10-22). */
  toggleOffSavesInactive: true,
  designInformation: true,
  systemFieldTiles: true,
  draftLabel: 'Draft',
  pageSizes: [5, 10, 25, 50],
  defaultPageSize: 25,
  importTemplateColumns: ['Template Name', 'Template ID', 'Created By', 'Last Modified', 'Status', 'Description'],
  exportFormats: ['xlsx', 'csv'],
  exportSubtitle: 'Choose the rows and columns to include in the exported file.',
  deleteMessage: 'This template will be permanently removed. Records already created from it are unaffected.',
  overviewMinimal: true,
  filterHidesHierarchy: true,
  savedDetail: {
    createSubtitle: 'Your Supplier Onboarding template is ready to use.',
    editSubtitle: 'Your changes to this template have been saved.',
    createInfo: 'Suppliers will now complete this form when they register.',
    editInfo:
      'Your changes are live. Suppliers registering from now on will see the updated form; submissions already '
      + 'received stay unchanged.',
  },

  /* The prototype's list: TEMPLATE CODE (design21, fixed), TEMPLATE NAME, CREATED BY, LAST MODIFIED, STATUS, ACTIONS. */
  codeColumn: true,
  columns: [
    { key: 'createdBy', header: 'CREATED BY', label: 'Created By', minWidth: 180, renderer: 'avatar', sortable: true, filter: { comparison: 'Contains', kind: 'text' } },
    { key: 'modifiedDate', header: 'LAST MODIFIED', label: 'Last Modified', minWidth: 150, renderer: 'text', sortable: true, filter: { comparison: 'Is after', kind: 'text' } },
    { key: 'statusName', header: 'STATUS', label: 'Status', minWidth: 130, renderer: 'statusPill', sortable: true, filter: { comparison: 'Is', kind: 'choice' } },
  ],
  /* What Template Information collects, listed under it in View -> Sections like any other section's fields. */
  informationFields: [
    { label: 'Template Name', type: 'Short text', required: true },
    { label: 'Business Unit', type: 'Dropdown', required: false },
    { label: 'Division', type: 'Dropdown', required: false },
    { label: 'Department', type: 'Dropdown', required: false },
    { label: 'Description', type: 'Long text', required: false },
    { label: 'Status', type: 'Toggle', required: false },
  ],

  steps: freezeMandatorySystemFields([
    {
      sectionKey: 'template-information',
      label: 'Template Information',
      icon: 'pi-info-circle',
      hint: 'Name the template, describe its purpose and set whether it is active.',
      fields: [],
    },
    {
      sectionKey: 'legal-and-company-details',
      label: 'Legal & Company Details',
      icon: 'pi-building',
      hint: 'Registration and legal identity of the supplier organisation.',
      fields: [
        { fieldKey: 'legal-business-name', label: 'Legal Business Name', fieldType: 'Text', helperText: 'Enter registered legal name', mandatory: true },
        { fieldKey: 'trade-name', label: 'Trade / DBA Name', fieldType: 'Text', helperText: 'Optional', mandatory: false },
        { fieldKey: 'business-structure', label: 'Business Structure', fieldType: 'Dropdown', helperText: 'Proprietorship / Partnership / LLP / Pvt Ltd / Public Ltd', mandatory: true, options: SUPPLIER_BUSINESS_STRUCTURE_OPTIONS },
        { fieldKey: 'company-registration-number', label: 'Company Registration Number', fieldType: 'Text', helperText: 'Enter registration number', mandatory: true },
        { fieldKey: 'tax-registration-number', label: 'Tax Registration Number', fieldType: 'Text', helperText: 'Region-configurable label · GSTIN / VAT / EIN', mandatory: true },
        { fieldKey: 'pan-number', label: 'PAN Number', fieldType: 'Text', helperText: 'Enter PAN (e.g. ABCDE1234F)', mandatory: true },
        { fieldKey: 'year-of-incorporation', label: 'Year of Incorporation', fieldType: 'Number', helperText: 'YYYY', mandatory: false },
        { fieldKey: 'website', label: 'Website', fieldType: 'URL', helperText: 'https://', mandatory: false },
        { fieldKey: 'registered-address', label: 'Registered Address', fieldType: 'Address block', helperText: 'Street, city, state, country, postal code', mandatory: true },
        /* The supplier's own Status (design21, 2026-10-22): frozen on; the wizard draws it as its Status switch. */
        { fieldKey: 'status', label: 'Status', fieldType: 'Toggle', helperText: 'Active / Inactive · default Active', mandatory: true },
      ],
    },
    {
      sectionKey: 'category-and-capability',
      label: 'Category & Capability',
      icon: 'pi-th-large',
      hint: 'What the supplier can provide and at what scale.',
      fields: [
        { fieldKey: 'commodity-interest', label: 'Category', fieldType: 'Multi-select Dropdown', helperText: 'From Category master', mandatory: true },
        { fieldKey: 'products', label: 'Products', fieldType: 'Multi-select Dropdown', helperText: 'From Product master', mandatory: true },
        { fieldKey: 'annual-turnover', label: 'Annual Turnover', fieldType: 'Currency', helperText: 'In payment currency', mandatory: false },
        { fieldKey: 'service-locations', label: 'Service Location', fieldType: 'Multi-select Dropdown', helperText: 'Select one or more locations', mandatory: true },
        { fieldKey: 'preferred-delivery-term', label: 'Preferred Delivery Term', fieldType: 'Dropdown', helperText: 'From Delivery Terms master', mandatory: false },
      ],
    },
    {
      /* The key stays `primary-contact` -- the onboarding service reads the contact by it. */
      sectionKey: 'primary-contact',
      label: 'Contact Details',
      icon: 'pi-address-book',
      hint: 'Who the procurement team deals with day to day.',
      fields: [
        { fieldKey: 'contact-person-name', label: 'Contact Person Name', fieldType: 'Text', helperText: 'Enter contact name', mandatory: true },
        { fieldKey: 'designation', label: 'Designation', fieldType: 'Text', helperText: 'Optional', mandatory: false },
        { fieldKey: 'email', label: 'Primary Email', fieldType: 'Email', helperText: 'name@company.com', mandatory: true },
        { fieldKey: 'secondary-email', label: 'Secondary Email', fieldType: 'Email', helperText: 'name@company.com', mandatory: false },
        { fieldKey: 'phone', label: 'Phone', fieldType: 'Phone', helperText: 'With country code', mandatory: true },
      ],
    },
    {
      sectionKey: 'banking-and-payment',
      label: 'Banking & Payment',
      icon: 'pi-building-columns',
      hint: 'Details required to raise payments to this supplier.',
      fields: [
        { fieldKey: 'bank-name', label: 'Bank Name', fieldType: 'Text', helperText: 'Enter bank name', mandatory: true },
        /* "Masked text" describes how the field renders to a supplier later. It is
           a label on a definition -- no account number is stored by this feature. */
        { fieldKey: 'account-number', label: 'Account Number', fieldType: 'Masked text', helperText: 'Last 4 digits shown', mandatory: true },
        { fieldKey: 'routing-number', label: 'IFSC / SWIFT / Routing Number', fieldType: 'Text', helperText: 'Enter IFSC, SWIFT or routing number', mandatory: true },
        { fieldKey: 'payment-currency', label: 'Payment Currency', fieldType: 'Dropdown', helperText: 'From Currency master · Default: INR', mandatory: true },
        { fieldKey: 'preferred-payment-terms', label: 'Preferred Payment Terms', fieldType: 'Dropdown', helperText: 'From Payment Terms master', mandatory: false },
        { fieldKey: 'bank-proof', label: 'Bank Proof', fieldType: 'File upload', helperText: 'Cancelled cheque or bank letter · PDF / JPG', mandatory: true },
      ],
    },
    {
      sectionKey: 'compliance-and-certifications',
      label: 'Compliance & Certifications',
      icon: 'pi-shield',
      hint: 'Certifications, declarations and references collected for compliance.',
      fields: [
        { fieldKey: 'msme-status', label: 'MSME / Small-Business / Diversity Status', fieldType: 'Dropdown', mandatory: false, options: SUPPLIER_MSME_OPTIONS },
        /*
          The only conditional fields in the module. What drives the condition is undefined in the reference, so
          the flag is stored and nothing evaluates it; the prototype draws no tag for it.
        */
        { fieldKey: 'quality-certifications', label: 'Quality Certifications', fieldType: 'Multi-select + file', helperText: 'ISO 9001 / ISO 14001 / IATF · certificate upload', mandatory: false, conditional: true },
        { fieldKey: 'insurance-certificate', label: 'Insurance Certificate', fieldType: 'File upload', helperText: 'Shown for works and services suppliers', mandatory: false, conditional: true },
      ],
    },
  ]),
  /* Read an older save's wording as today's (the migration rewrites stored defaults; this covers a stale read). */
  legacyWording: (text) =>
    ({
      'Primary Contact': 'Contact Details',
      'Category / Commodity Interest': 'Category',
      'Manufacturing / Service Locations': 'Service Location',
      'Select one or more product on basis of selected category and locations': 'From Product master',
      'Cancelled cheque or bank letter': 'Cancelled cheque or bank letter · PDF / JPG',
    })[text.replace(/Â·/g, '·')] ?? text.replace(/Â·/g, '·'),
};

